/**
 * scanConfidence.ts — what a scan has to have EARNED before it pre-fills a
 * field. The web half of artifacts/mobile/lib/scanConfidence.ts.
 *
 * ── THIS IS A SECOND COPY OF A RULE, WHICH THAT FILE ARGUES AGAINST ────────
 *
 * Its header says a threshold is "exactly the shape that drifts, because
 * tuning it is a one-character edit that looks complete after the first file",
 * and cites this codebase paying for the alternative twice — `listingFormRules`
 * exists because two copies of a rule produced 22 confirmed divergences. That
 * argument is right and it applies here.
 *
 * 🔑 SO WHY A COPY. `artifacts/support` and `artifacts/mobile` are separate
 * workspace packages that share code only through `lib/*` packages, and adding
 * one means a new package.json, a workspace link and a `pnpm install` — the
 * lockfile is not this branch's to move. The honest options were therefore a
 * copy that can drift silently, or a copy that CANNOT.
 *
 * scanConfidenceParity.test.ts is what makes it the second kind: it reads
 * mobile's source off disk, parses both constants out of it, and asserts they
 * equal these. Tune either number in either file and that test goes red naming
 * the other. It is the same technique demoContract.test.ts uses to pin app.js
 * from the TypeScript side — a cross-file anchor, not a convention.
 *
 * ⚠️ IF YOU ARE HERE TO CHANGE A NUMBER: change it in BOTH files in the same
 * commit, and read mobile's header first. The reasoning for the gap between
 * the two bars lives there and is not duplicated here, deliberately — one
 * home for the argument, two homes for the value, and a test tying them
 * together.
 *
 * ── WHAT `confidence` IS, AND THE OTHER ONE IT IS NOT ──────────────────────
 *
 * 0-100, the MODEL's own self-report, defined narrowly by the prompt
 * (api-server/src/lib/aiTools.ts): how sure it is that `title` names the EXACT
 * item — this model, set or edition — not merely its category. It is a
 * statement about the IDENTIFICATION.
 *
 * ⚠ NOT `CatalogMatch.confidence`, which is 0..1 and is one VENDOR candidate's
 * likelihood of being the item. Same word, two scales, two subjects. Nothing
 * here may be compared against, derived from, or rescaled into that one.
 */

/**
 * Below this, the scan does NOT pre-fill the PRICE.
 *
 * A first guess to be tuned once there is real data — see mobile's header for
 * the 2026-09-08 experiment that justifies the SHAPE rather than the precision.
 */
export const MIN_CONFIDENCE_TO_PREFILL_VALUE = 60;

/**
 * Below this, the scan does NOT pre-fill the fields that only mean anything if
 * the item was IDENTIFIED — today `title` and `brand`.
 *
 * 20 is the prompt's own number: it says, verbatim, "If you can only tell what
 * KIND of thing this is, say so honestly with a number under 20". A reply under
 * 20 is the model stating on its own terms that it did not identify the item.
 * If that sentence changes, both files change with it.
 */
export const MIN_CONFIDENCE_TO_PREFILL_IDENTITY = 20;

/**
 * Did the model claim to have identified the item well enough to pre-fill the
 * identity fields?
 *
 * ⚠ `typeof === "number"`, NEVER `(confidence ?? 0) >= …`. Mobile shipped the
 * price gate in that second form once and it was the loosest check in the file
 * on the one field that gates money: `??` defends only against null/undefined
 * and then hands whatever else arrived to a COERCING `>=`, so the string "85"
 * — and "999", and " 85 " — sailed through and pre-filled. The `typeof` test
 * makes NaN, null, undefined and every non-number fail closed for free, and
 * the explicit upper bound rejects a nonsense 150 rather than reading it as
 * maximum confidence.
 *
 * The parameter is `unknown` ON PURPOSE. `ScanSuggestion.confidence` is typed
 * `number | undefined`, but `awaitScan` does no runtime validation — so the
 * type here says what actually arrives, not what the declaration promises.
 */
export function scanIdentifiedTheItem(confidence: unknown): boolean {
  return (
    typeof confidence === "number" &&
    confidence >= MIN_CONFIDENCE_TO_PREFILL_IDENTITY &&
    confidence <= 100 // the documented top of the 0-100 scale
  );
}

/** The same test at the PRICE's bar. See both constants above, and mobile's
 *  header, for why these are two numbers and not one. */
export function scanEarnedItsPrice(confidence: unknown): boolean {
  return (
    typeof confidence === "number" &&
    confidence >= MIN_CONFIDENCE_TO_PREFILL_VALUE &&
    confidence <= 100
  );
}
